import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Users, ArrowRight, ArrowLeft as MoveLeft, Clock, UserPlus, UserMinus, AlertTriangle, GripVertical, Search, X, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

function SundayTeamAllocation() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [teamA, setTeamA] = useState([]);
  const [teamB, setTeamB] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showReorder, setShowReorder] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [selectedWaitlistIds, setSelectedWaitlistIds] = useState([]);
  const [selectedNewUserIds, setSelectedNewUserIds] = useState([]);
  const [selectedRemoveIds, setSelectedRemoveIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [matchRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches/${matchId}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/all`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      const matchData = matchRes.data;
      setMatch(matchData);
      setAllUsers(usersRes.data);

      // Get all RSVPs with status "in", sorted by timestamp (FCFS)
      const inRsvps = (matchData.rsvps || [])
        .filter(r => r.status === 'in')
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Split into selected players and waitlist
      const selectedPlayerIds = matchData.selected_players || [];

      // Selected players (maintain order from selected_players array)
      const selectedRsvps = selectedPlayerIds
        .map(id => inRsvps.find(r => r.user_id === id))
        .filter(Boolean);

      // Waitlist: players who RSVP'd "in" but are not in selected_players
      const waitlistRsvps = inRsvps.filter(r => !selectedPlayerIds.includes(r.user_id));

      // Split selected into teams and unassigned
      const aPlayers = selectedRsvps.filter(r => matchData.team_a?.includes(r.user_id));
      const bPlayers = selectedRsvps.filter(r => matchData.team_b?.includes(r.user_id));
      const unassignedPlayers = selectedRsvps.filter(r => 
        !matchData.team_a?.includes(r.user_id) && !matchData.team_b?.includes(r.user_id)
      );

      setTeamA(aPlayers);
      setTeamB(bPlayers);
      setUnassigned(unassignedPlayers);
      setWaitlist(waitlistRsvps);
      setReorderList(selectedRsvps);
    } catch (error) {
      toast.error('Failed to load match');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const moveToTeamA = (player) => {
    setUnassigned(unassigned.filter(p => p.user_id !== player.user_id));
    setTeamB(teamB.filter(p => p.user_id !== player.user_id));
    setTeamA([...teamA, player]);
  };

  const moveToTeamB = (player) => {
    setUnassigned(unassigned.filter(p => p.user_id !== player.user_id));
    setTeamA(teamA.filter(p => p.user_id !== player.user_id));
    setTeamB([...teamB, player]);
  };

  const moveToUnassigned = (player) => {
    setTeamA(teamA.filter(p => p.user_id !== player.user_id));
    setTeamB(teamB.filter(p => p.user_id !== player.user_id));
    setUnassigned([...unassigned, player]);
  };

  const handleAddFromWaitlist = async (player) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/matches/${matchId}/admin-add-player/${player.user_id}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(`${player.user?.name || 'Player'} added to selected players`);
      await fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to add player';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNewPlayer = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/matches/${matchId}/admin-add-and-select/${userId}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Player added!');
      setShowAddPlayer(false);
      setSearchQuery('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleWaitlistSelection = (userId) => {
    setSelectedWaitlistIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleBulkAddFromWaitlist = async () => {
    if (selectedWaitlistIds.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/matches/${matchId}/bulk-add-from-waitlist`,
        { user_ids: selectedWaitlistIds },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(res.data.message);
      setSelectedWaitlistIds([]);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add players');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleNewUserSelection = (userId) => {
    setSelectedNewUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleBulkAddNewPlayers = async () => {
    if (selectedNewUserIds.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/matches/${matchId}/bulk-add-new-players`,
        { user_ids: selectedNewUserIds },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(res.data.message);
      setSelectedNewUserIds([]);
      setShowAddPlayer(false);
      setSearchQuery('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add players');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleRemoveSelection = (userId) => {
    setSelectedRemoveIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleBulkRemove = async () => {
    if (selectedRemoveIds.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/matches/${matchId}/bulk-remove-players`,
        { user_ids: selectedRemoveIds },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(res.data.message);
      setSelectedRemoveIds([]);
      await fetchData();
    } catch (error) {
      toast.error('Failed to remove players');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFromSelected = async (player) => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/matches/${matchId}/admin-remove-player/${player.user_id}`,
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(`${player.user?.name || 'Player'} moved to waitlist`);
      await fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to remove player';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const movePlayerUp = (index) => {
    if (index === 0) return;
    const newList = [...reorderList];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setReorderList(newList);
  };

  const movePlayerDown = (index) => {
    if (index === reorderList.length - 1) return;
    const newList = [...reorderList];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setReorderList(newList);
  };

  const handleSaveReorder = async () => {
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/matches/${matchId}/reorder-players`,
        { player_ids: reorderList.map(p => p.user_id) },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Player order updated!');
      setShowReorder(false);
      await fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${API_URL}/api/matches/${matchId}/allocate-teams`,
        {
          team_a: teamA.map(p => p.user_id),
          team_b: teamB.map(p => p.user_id)
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Teams allocated successfully!');
      navigate('/admin');
    } catch (error) {
      toast.error('Failed to allocate teams');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const totalSelected = teamA.length + teamB.length + unassigned.length;
  const maxPlayers = match?.max_players || 24;
  const spotsLeft = maxPlayers - totalSelected;

  // Users who have RSVP'd IN
  const rsvpUserIds = [...(match?.rsvps || [])].filter(r => r.status === 'in').map(r => r.user_id);
  
  // Users who can be added (not already RSVP'd IN)
  const availableToAdd = allUsers.filter(u => !rsvpUserIds.includes(u.user_id));
  
  // Filter by search
  const filteredUsers = availableToAdd.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white pt-[env(safe-area-inset-top)]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">SUNDAY TEAM ALLOCATION</h1>
              {match && (
                <p className="text-xs text-slate-300">
                  {format(new Date(match.date), 'dd MMM yyyy')} | {totalSelected}/{maxPlayers} selected
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={handleSave}
            className="bg-[#E63946] hover:bg-[#DC2626] text-white font-bold"
            data-testid="save-teams-btn"
          >
            Save Teams
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Admin Actions */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setShowAddPlayer(true)}
            variant="outline"
            className="border-dashed border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946]/5"
            data-testid="add-player-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Player
          </Button>
          <Button
            onClick={() => { setReorderList([...teamA, ...teamB, ...unassigned].length > 0 ? [...teamA, ...teamB, ...unassigned] : reorderList); setShowReorder(true); }}
            variant="outline"
            className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100"
            data-testid="reorder-btn"
          >
            <GripVertical className="w-4 h-4 mr-2" />
            Adjust Order
          </Button>
          {selectedRemoveIds.length > 0 && (
            <Button
              onClick={handleBulkRemove}
              disabled={actionLoading}
              variant="outline"
              className="border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white"
              data-testid="bulk-remove-btn"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Remove Selected ({selectedRemoveIds.length})
            </Button>
          )}
        </div>

        {/* Add Player Modal */}
        {showAddPlayer && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-lg">Add Player</h3>
                <Button
                  onClick={() => { setShowAddPlayer(false); setSearchQuery(''); setSelectedNewUserIds([]); }}
                  variant="ghost"
                  size="icon"
                  data-testid="close-add-player-modal-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 overflow-y-auto">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, phone, or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-player-input"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {filteredUsers.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No players found</p>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user.user_id}
                        className="p-3 border border-slate-200 rounded-lg flex items-center justify-between hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedNewUserIds.includes(user.user_id)}
                            onChange={() => toggleNewUserSelection(user.user_id)}
                            className="w-4 h-4 accent-[#E63946]"
                            data-testid={`new-player-select-${user.user_id}`}
                          />
                          <div>
                            <p className="font-semibold text-[#0F172A]">{user.name}</p>
                            <p className="text-xs text-slate-500">{user.cricket_role} • {user.phone}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleAddNewPlayer(user.user_id)}
                          size="sm"
                          disabled={actionLoading || spotsLeft <= 0}
                          className="bg-[#E63946] hover:bg-[#DC2626]"
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {selectedNewUserIds.length > 0 && (
                <div className="p-4 border-t border-slate-200">
                  <Button
                    onClick={handleBulkAddNewPlayers}
                    disabled={actionLoading || spotsLeft <= 0}
                    className="w-full bg-[#E63946] hover:bg-[#DC2626]"
                    data-testid="bulk-add-new-players-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Selected ({selectedNewUserIds.length})
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {showReorder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-lg">Adjust Player Order</h3>
                <Button
                  onClick={() => setShowReorder(false)}
                  variant="ghost"
                  size="icon"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-600 mb-4">
                  Drag players to change their order. This affects FCFS priority.
                </p>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {reorderList.map((player, index) => (
                    <div
                      key={player.user_id}
                      className="p-3 border border-slate-200 rounded-lg flex items-center gap-3 bg-slate-50"
                    >
                      <span className="text-sm font-bold text-slate-500 w-6">#{index + 1}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-[#0F172A] text-sm">{player.user?.name}</p>
                        <p className="text-xs text-slate-500">{player.user?.cricket_role}</p>
                      </div>
                      <div className="flex flex-col">
                        <Button
                          onClick={() => movePlayerUp(index)}
                          variant="ghost"
                          size="sm"
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => movePlayerDown(index)}
                          variant="ghost"
                          size="sm"
                          disabled={index === reorderList.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleSaveReorder}
                  disabled={actionLoading}
                  className="w-full mt-4 bg-[#E63946] hover:bg-[#DC2626]"
                  data-testid="save-order-btn"
                >
                  Save Order
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Waitlist Section */}
        {waitlist.length > 0 && (
          <div className="bg-amber-50 rounded-xl border-2 border-amber-300 p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-lg font-bold text-amber-800 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Waitlist ({waitlist.length})
                {spotsLeft > 0 && (
                  <span className="text-sm font-normal text-amber-600 ml-2">
                    {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} available
                  </span>
                )}
              </h2>
              {selectedWaitlistIds.length > 0 && (
                <Button
                  onClick={handleBulkAddFromWaitlist}
                  size="sm"
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-add-waitlist-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add Selected ({selectedWaitlistIds.length})
                </Button>
              )}
            </div>
            <p className="text-sm text-amber-700 mb-4">
              Players who RSVP'd "IN" but are not yet selected. Check multiple to add together, or use the + button individually.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {waitlist.map((player, idx) => (
                <WaitlistPlayerCard
                  key={player.user_id}
                  player={player}
                  position={idx + 1}
                  onAdd={() => handleAddFromWaitlist(player)}
                  canAdd={spotsLeft > 0}
                  loading={actionLoading}
                  isSelected={selectedWaitlistIds.includes(player.user_id)}
                  onToggleSelect={() => toggleWaitlistSelection(player.user_id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Team Allocation Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team A */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-red-500 p-5">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-red-600" />
              Team A - Bhandball ({teamA.length})
            </h2>
            <div className="space-y-2">
              {teamA.map((player) => (
                <PlayerCard 
                  key={player.user_id} 
                  player={player} 
                  onMove={moveToUnassigned}
                  onRemove={() => handleRemoveFromSelected(player)}
                  teamColor="red"
                  showRemove={true}
                  loading={actionLoading}
                  isSelected={selectedRemoveIds.includes(player.user_id)}
                  onToggleSelect={() => toggleRemoveSelection(player.user_id)}
                />
              ))}
              {teamA.length === 0 && (
                <p className="text-slate-500 text-center py-8">No players assigned</p>
              )}
            </div>
          </div>

          {/* Unassigned Selected Players */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-slate-300 p-5">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Selected Players ({unassigned.length})
            </h2>
            <div className="space-y-2">
              {unassigned.map((player) => (
                <UnassignedPlayerCard 
                  key={player.user_id} 
                  player={player} 
                  onMoveToA={() => moveToTeamA(player)}
                  onMoveToB={() => moveToTeamB(player)}
                  onRemove={() => handleRemoveFromSelected(player)}
                  loading={actionLoading}
                  isSelected={selectedRemoveIds.includes(player.user_id)}
                  onToggleSelect={() => toggleRemoveSelection(player.user_id)}
                />
              ))}
              {unassigned.length === 0 && (
                <p className="text-green-600 text-center py-8 font-semibold">All players assigned to teams!</p>
              )}
            </div>
          </div>

          {/* Team B */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-blue-500 p-5">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Team B - Kacche Khiladi ({teamB.length})
            </h2>
            <div className="space-y-2">
              {teamB.map((player) => (
                <PlayerCard 
                  key={player.user_id} 
                  player={player} 
                  onMove={moveToUnassigned}
                  onRemove={() => handleRemoveFromSelected(player)}
                  teamColor="blue"
                  showRemove={true}
                  loading={actionLoading}
                  isSelected={selectedRemoveIds.includes(player.user_id)}
                  onToggleSelect={() => toggleRemoveSelection(player.user_id)}
                />
              ))}
              {teamB.length === 0 && (
                <p className="text-slate-500 text-center py-8">No players assigned</p>
              )}
            </div>
          </div>
        </div>

        {/* Warning if unassigned players exist */}
        {unassigned.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Players not assigned to teams</p>
              <p className="text-sm text-amber-700">
                {unassigned.length} player{unassigned.length !== 1 ? 's are' : ' is'} selected but not assigned to either team. 
                Assign them before generating the team sheet.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WaitlistPlayerCard({ player, position, onAdd, canAdd, loading, isSelected, onToggleSelect }) {
  return (
    <div className={`p-3 rounded-lg border flex items-center justify-between ${isSelected ? 'border-green-400 bg-green-50' : 'border-amber-200 bg-white'}`}>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-4 h-4 accent-green-600"
          data-testid={`waitlist-select-${player.user_id}`}
        />
        <span className="text-sm font-bold text-amber-600 w-6">#{position}</span>
        <div>
          <p className="font-semibold text-[#0F172A] text-sm">{player.user?.nickname || player.user?.name}</p>
          <p className="text-xs text-slate-500">{player.user?.cricket_role}</p>
        </div>
      </div>
      <Button
        onClick={onAdd}
        size="sm"
        disabled={!canAdd || loading}
        className="bg-green-600 hover:bg-green-700 text-white h-8 w-8 p-0"
        data-testid="add-from-waitlist-btn"
        title={canAdd ? "Add to selected players" : "Match is full"}
      >
        <UserPlus className="w-4 h-4" />
      </Button>
    </div>
  );
}

function PlayerCard({ player, onMove, onRemove, teamColor, showRemove, loading, isSelected, onToggleSelect }) {
  return (
    <div className={`p-3 rounded-lg border-2 ${
      teamColor === 'red' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
    } flex items-center justify-between ${isSelected ? 'ring-2 ring-red-400' : ''}`}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-4 h-4 accent-red-600"
          data-testid={`select-remove-${player.user_id}`}
        />
        <div>
          <p className="font-semibold text-[#0F172A]">{player.user?.nickname || player.user?.name}</p>
          <p className="text-xs text-slate-600">{player.user?.cricket_role}</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          onClick={() => onMove(player)}
          size="sm"
          variant="ghost"
          className="text-slate-600 hover:text-[#0F172A] h-8 w-8 p-0"
          data-testid="unassign-player-btn"
          title="Move to unassigned"
        >
          <MoveLeft className="w-4 h-4" />
        </Button>
        {showRemove && (
          <Button
            onClick={onRemove}
            size="sm"
            variant="ghost"
            disabled={loading}
            className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
            data-testid="remove-player-btn"
            title="Remove from match"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function UnassignedPlayerCard({ player, onMoveToA, onMoveToB, onRemove, loading, isSelected, onToggleSelect }) {
  return (
    <div className={`p-3 rounded-lg border bg-slate-50 ${isSelected ? 'ring-2 ring-red-400 border-red-300' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 accent-red-600"
            data-testid={`select-remove-${player.user_id}`}
          />
          <div>
            <p className="font-semibold text-[#0F172A]">{player.user?.nickname || player.user?.name}</p>
            <p className="text-xs text-slate-600">{player.user?.cricket_role}</p>
          </div>
        </div>
        <Button
          onClick={onRemove}
          size="sm"
          variant="ghost"
          disabled={loading}
          className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
          data-testid="remove-unassigned-btn"
          title="Remove from match"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={onMoveToA}
          size="sm"
          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs"
          data-testid="assign-team-a-btn"
        >
          → Team A
        </Button>
        <Button
          onClick={onMoveToB}
          size="sm"
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs"
          data-testid="assign-team-b-btn"
        >
          → Team B
        </Button>
      </div>
    </div>
  );
}

export default SundayTeamAllocation;
